<script setup>

// Imports
import { useGraphQL } from '@/composables/useGraphQL'
import { assertPageFound, hasPageBuilderContent } from '@/composables/useAssertPageFound'
import { usePreview } from '@/composables/usePreview'
import { useSeomatic } from '@/composables/useSeomatic'

import { useHead, computed, defineAsyncComponent } from '#imports'

import PopupForm from '~/components/globals/popup/form.vue'
const SecondaryHeader = defineAsyncComponent(() =>
  import('./header/index.vue')
)

import { PAGE_QUERY } from '@/queries/page'
import { SEOMATIC_QUERY } from '@/queries/seomatic'
import { ADDITIONAL_LANGUAGES_QUERY } from '@/queries/additional-languages'

import { generatePageJsonLdScripts } from '@/services/jsonld'
import { queryPageWithSchemaOverride } from '@/services/jsonld/pageQuery'
import { useBreadcrumbEntries } from '@/composables/useBreadcrumbEntries'
import { generateVideoObjectFromHeroSection1 } from '@/services/jsonld/videoObjectFromHeroSection1'
import { generateVideoObjectFromHeroSection2 } from '@/services/jsonld/videoObjectFromHeroSection2'
import { generateVideoObjectFromHeroSideImage } from '@/services/jsonld/videoObjectFromHeroSideImage'
import { generateVideoObjectFromTextAndMedia } from '@/services/jsonld/videoObjectFromTextAndMedia'
import { getFontPreloads } from '@/composables/useFontPreload'
import { normalizeRouteSlugParam, toCmsUri } from '@/utils/cms-route-uri'
import { isLocaleHomeSlug } from '@/utils/site-handle'

const props = defineProps({
  navigation: {
    type: Object,
    required: false,
    default: () => ({})
  },
  footer: {
    type: Object,
    required: false,
    default: () => ({})
  },
  banner: {
    type: Object,
    required: false,
    default: () => ({})
  },
  siteHandle: {
    type: [String],
    required: true,
  }
})

const route = useRoute()
const graphql = useGraphQL()
const { isPreview, previewToken } = usePreview()
// True only on the isolated Page Builder canvas instance (NUXT_PUBLIC_PB_BUILDER=1). Used to skip work
// that's pointless while editing (SEO meta / hreflang fetches), keeping post-edit refreshes fast.
const { pbBuilder } = useRuntimeConfig().public

// Get the current slug from the route
const slug = computed(() => {
  return normalizeRouteSlugParam(route.params.slug)
})

// Normalized page slug
const cmsUri = computed(() => {
  if (toCmsUri(slug.value) === '__home__') return '__home__'
  if (isLocaleHomeSlug(slug.value)) return '__home__'

  return slug.value
})

// Normalized page slug
const cmsSlug = computed(() => {
  if (!slug.value) return '__home__'
  if (isLocaleHomeSlug(slug.value)) return '__home__'

  const parts = String(slug.value).split('/').filter(Boolean)
  const lastSegment = parts.length ? parts[parts.length - 1] : ''
  return lastSegment || '__home__'
})

const shouldAutoOpenDemoRequestForm = computed(() => {
  return /^(?:demo-request(?:-(?:shopping|booking|support|retention|midas))?|request-demo)$/.test(cmsSlug.value)
})

const demoRequestSessionStorageKey = 'delightDemoRequestFormSubmitted'

const customStylesheet = computed(() => {
  const slug = cmsSlug.value

  // Competitor comparison pages share hero / metrics-bar chrome. Comparison
  // table styling lives on the block itself and is not slug-gated.
  if (slug.endsWith('-vs-delight') || slug.startsWith('delight-vs-')) {
    return '/css/delight-ai-vs-decagon.css'
  }

  if (slug === 'ai-readiness' || slug === 'ai-readiness-intro' || slug === 'ai-readiness-page') {
    return '/css/ai-readiness-page.css'
  }

})

// Fetch the page data
// Use cmsUri for cache key to ensure consistency between SSR and client hydration
const { data: pageResponse, error } = await useAsyncData(
  `page-${cmsUri.value}-${props.siteHandle}${previewToken.value ? `-${previewToken.value}` : ''}`,
  async () => {
    // Critical path: fetch only page so preview can render ASAP.
    let pageResult
    try {
      pageResult = await queryPageWithSchemaOverride(document => graphql.query(
        document,
        {
          site: [props.siteHandle],
          uri: cmsUri.value,
        },
        {
          previewToken: previewToken.value
        }
      ), PAGE_QUERY)
    } catch (err) {
      // During SSG generation, re-throw errors to stop deployment
      const isGenerating = typeof window === 'undefined' && process.env['NODE_ENV'] === 'production'
      
      if (isGenerating) {
        throw err
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      console.error('Error fetching page:', errorMessage)
      return {
        page: null,
        found: false
      }
    }

    const page = pageResult?.entry
    if (!page) {
      return {
        page: null,
        found: false
      }
    }
    return {
      page: page,
      found: true
    }
  },
  
  {
    default: () => ({
      page: null,
      found: false
    }),
  }
)

// Check for errors during SSG generation
const isGenerating = typeof window === 'undefined' && process.env['NODE_ENV'] === 'production'
if (isGenerating && error.value) {
  throw error.value
}

assertPageFound(
  !pageResponse.value?.found
    || !pageResponse.value?.page
    || !hasPageBuilderContent(pageResponse.value.page),
  cmsUri.value,
)

const pageFound = computed(() => pageResponse.value?.found ?? false)
const pageData = computed(() => {
  // Return null if page not found or data not loaded yet (prevents flicker during hydration)
  if (!pageFound.value || !pageResponse.value?.page) {
    return null
  }
  return pageResponse.value.page
})

const supplementaryDefaultData = {
  seomatic: {},
  additionalLanguages: []
}

const fetchSupplementaryData = async () => {
  // In the Page Builder canvas, SEO meta + hreflang don't matter (it's an editing view, not the
  // published page). Skip both GraphQL round-trips entirely — on the initial render AND on every
  // post-edit refetch the pageData watch would otherwise trigger — so editing stays snappy. Consumers
  // already fall back to this empty default.
  if (pbBuilder || !pageData.value?.id || !pageFound.value) {
    return supplementaryDefaultData
  }

  const [seomaticResult, additionalLanguagesResult] = await Promise.allSettled([
    graphql.query(
      SEOMATIC_QUERY,
      {
        site: props.siteHandle,
        uri: cmsUri.value,
      },
      {
        previewToken: previewToken.value
      }
    ),
    graphql.query(
      ADDITIONAL_LANGUAGES_QUERY,
      { id: [pageData.value.id] },
      {
        previewToken: previewToken.value
      }
    )
  ])

  const seomatic = seomaticResult.status === 'fulfilled'
    ? (seomaticResult.value?.seomatic ?? {})
    : {}
  const additionalLanguages = additionalLanguagesResult.status === 'fulfilled'
    ? (additionalLanguagesResult.value?.entries ?? [])
    : []

  return {
    seomatic,
    additionalLanguages
  }
}

const supplementaryKey = computed(() => `page-supp-${cmsUri.value}-${props.siteHandle}${previewToken.value ? `-${previewToken.value}` : ''}`)
const supplementaryOptions = {
  default: () => supplementaryDefaultData,
  watch: [pageData, () => props.siteHandle, cmsUri, previewToken]
}

const supplementarySource = isPreview.value
  ? useLazyAsyncData(supplementaryKey.value, fetchSupplementaryData, {
      ...supplementaryOptions,
      server: false
    })
  : await useAsyncData(supplementaryKey.value, fetchSupplementaryData, {
      ...supplementaryOptions,
      server: true
    })

const supplementaryData = supplementarySource.data

// Lazy load NotFoundSection only when needed
const showNotFound = computed(() => {
  if (pbBuilder) {
    return false
  }
  return !pageFound.value || !pageData.value || !(pageData.value.hero?.length || pageData.value.pageBuilder?.length)
})
const NotFoundSection = defineAsyncComponent(() => import('~/components/globals/notFoundSection.vue'))
const additionalLanguages = computed(() => supplementaryData.value?.additionalLanguages ?? [])


// Combine hero and pageBuilder blocks with source information
const pageBuilderBlocks = computed(() => {
  const heroBlocks = (pageData.value?.hero || []).map(block => ({
    ...block,
    source: 'hero'
  }))
  
  const pageBuilderBlocks = (pageData.value?.pageBuilder || []).map(block => ({
    ...block,
    source: 'pageBuilder'
  }))
  
  return [...heroBlocks, ...pageBuilderBlocks]
})

// Use seomatic composable
const seomaticData = computed(() => supplementaryData.value?.seomatic ?? {})
const { seomaticTitle, seomaticMetaTags, seomaticHeadMeta, seomaticHeadScripts, seomaticBodyScripts } = useSeomatic(seomaticData)

// Generate JSON-LD structured data scripts
const { breadcrumbEntries } = await useBreadcrumbEntries(() => props.siteHandle)

const jsonLdScripts = computed(() => generatePageJsonLdScripts({
  breadcrumbEntries: breadcrumbEntries.value,
  path: route.path,
  siteHandle: props.siteHandle,
  entry: pageData.value,
  metaTags: seomaticMetaTags.value,
  kind: 'page',
  videos: [
    generateVideoObjectFromHeroSection1(pageData.value?.hero || []),
    generateVideoObjectFromHeroSection2(pageData.value?.hero || []),
    generateVideoObjectFromHeroSideImage(pageBuilderBlocks.value),
    generateVideoObjectFromTextAndMedia(pageData.value?.pageBuilder || []),
  ],
}))

// Set the page title and meta tags
useHead(() => {
  // Get font preloads for critical fonts
  const fontPreloads = getFontPreloads(cmsUri.value, props.siteHandle)
  
  const link = [
    ...fontPreloads.map((preload, index) => ({
      rel: 'preload',
      href: preload.href,
      as: preload.as,
      type: preload.type,
      crossorigin: preload.crossorigin,
      key: `font-preload-${index}`
    })),
  ]

  if (customStylesheet.value) {
    link.push({
      rel: 'stylesheet',
      href: customStylesheet.value,
      key: 'page-custom-stylesheet'
    })
  }
  
  // Font stylesheet should always load, even if page not found
  const head = {
    link
  }

  // Don't set other head data if page not found
  if (!pageFound.value || !pageData.value || pageData.value.id === 'not-found') {
    return head
  }
  
  head.title = seomaticTitle.value || pageData.value?.title || 'Page Not Found'

  // Add meta tags from seomatic
  if (seomaticHeadMeta.value.length > 0) {
    head.meta = seomaticHeadMeta.value
  }

  // Add head scripts from seomatic
  if (seomaticHeadScripts.value.length > 0) {
    head.script = seomaticHeadScripts.value
  }
  
  // Add JSON-LD structured data scripts
  if (jsonLdScripts.value.length > 0) {
    const existingScripts = head.script || []
    head.script = [
      ...existingScripts,
      ...jsonLdScripts.value
    ]
  }

  return head
})

// Add body scripts from seomatic
useHead(() => seomaticBodyScripts.value)

const pageButtonsColor = computed(() => {
  const color = pageData.value?.pageButtonsColor
  return typeof color === 'string' && color.trim() ? color.trim() : null
})

const effectiveBanner = computed(() => {
  const pageBanner = pageData.value?.banner
  if (pageBanner?.bannerText?.html) {
    return { banner: pageBanner }
  }
  return props.banner
})

const pageClasses = computed(() => ({
  '--overlapping-header': pageData.value?.overlappingHeader,
  '--overlapping-header-dark': pageData.value?.overlappingHeader && !pageData.value?.overlappingHeaderDark,
  '--has-background-color': pageData.value?.backgroundColor || '',
  '--page-buttons-color': pageButtonsColor.value,
}))

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <div class="page default-page"
    :class="pageClasses"
    :data-page-id="pageData?.id || ''"
    :data-header-visibility-options="pageData?.headerVisibilityOptions || ''"
    :style="{
      backgroundColor: pageData?.backgroundColor || '',
      ...(pageButtonsColor ? { '--page-buttons-color': pageButtonsColor } : {}),
    }"
  >

    <LayoutBanner
      v-if="!pageData?.headerVisibilityOptions || !['noTopBanner', 'noTopBannerNoNavigation', 'hideCtasAndHideBanner', 'hideEverything', 'showOnlyCta', 'showOnlyCtaWithoutLangSwitcher'].includes(pageData.headerVisibilityOptions)"
      :banner="effectiveBanner"
    />

    <SecondaryHeader
      v-if="pageData?.secondaryHeaderNavigation?.length"
      :navigation="pageData?.secondaryHeaderNavigation[0]?.children"
      :siteHandle="props.siteHandle"
    />
    <LayoutHeader
      v-else
      :navigation="props.navigation"
      :additionalLanguages="additionalLanguages"
      :siteHandle="props.siteHandle"
      :visibilityOptions="pageData?.headerVisibilityOptions"
      :custom-header-cta="pageData?.customHeaderCta"
      :global-cta-settings="{
        popupEnabled: pageData?.popupEnabled,
        textOverride: pageData?.globalCtaTextOverride,
      }"
    />

    <main id="main" tabindex="-1">

      <div v-if="(pageData?.image || [])[0]?.url" class="page-bg">
        <GlobalsImage
          :landscape="pageData?.image"
          :portrait="pageData?.imageMobile"
          loading="eager"
          aria-hidden="true"
        />
      </div>
      
      <GlobalsPageBuilder
        v-if="pageFound && pageData && (pageData.hero?.length || pageData.pageBuilder?.length)"
        :pageBuilder="pageBuilderBlocks"
      />

      <component
        v-else-if="showNotFound"
        :is="NotFoundSection"
        :site-handle="props.siteHandle"
      />
      
    </main>

    <PopupForm
      v-if="
        pageData?.formPopup?.length &&
        pageData?.formPopup[0]?.formSettings?.formId?.length
      "
      :formSettings="pageData?.formPopup[0]?.formSettings"
      :autoOpenOnMount="shouldAutoOpenDemoRequestForm"
      :submissionSessionStorageKey="shouldAutoOpenDemoRequestForm ? demoRequestSessionStorageKey : ''"
    />

    <LayoutFooter
      :footer="props.footer"
      :additionalLanguages="additionalLanguages"
      :siteHandle="props.siteHandle"
      :hideFooterNav="pageData?.hideFooterNavigation"
      :global-cta-settings="{
        popupEnabled: pageData?.popupEnabled,
        textOverride: pageData?.globalCtaTextOverride,
      }"
    />
  </div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

main {
  position: relative;
  z-index: 1;
}

.page-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
}

.page-bg :deep(img) {
  width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
}

</style>

<style>

.default-page.--page-buttons-color .secondary-header .btn-bg,
.default-page.--page-buttons-color .footer-title-wrap .btn-bg,
.default-page.--page-buttons-color .cta.cta-contained .btn-primary,
.default-page.--page-buttons-color .cta.cta-contained .btn-bg,
.default-page.--page-buttons-color .btn-primary.header-cta-button .btn-bg,
.default-page.--page-buttons-color .hero-side-asset__button,
.default-page.--page-buttons-color .hero-side-asset__button .btn-bg,
.default-page.--page-buttons-color .hero-side-asset__heading-table-item-icon {
  background: var(--page-buttons-color) !important;
}

.default-page.--page-buttons-color .btn.text-and-media__button,
.default-page.--page-buttons-color .btn.text-and-image__button,
.default-page.--page-buttons-color .btn.btn-transparent,
.default-page.--page-buttons-color .btn.carousel-thumbs__card-quote-button {
  border-color: var(--page-buttons-color) !important;
  color: var(--page-buttons-color) !important;
}

</style>
